import React from 'react';

const UserResponsibilities: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-lg font-semibold">Acceptable Use Guidelines</h4>
        <p>
          By using FeelHeard.me, you agree to engage with our AI companion in a manner that respects 
          the intended purpose of the service. Our platform is designed to provide emotional support 
          through compassionate listening and reflection.
        </p>
        
        <div className="bg-[#A9D0A1]/10 p-4 rounded-lg">
          <h5 className="font-medium mb-2">Examples of Appropriate Use:</h5>
          <ul className="list-disc pl-5 space-y-2">
            <li>Discussing everyday stressors and seeking emotional validation</li>
            <li>Processing feelings about personal or professional challenges</li>
            <li>Practicing emotional articulation and reflection</li>
            <li>Seeking perspective on interpersonal situations</li>
            <li>Using the service as a complement to professional mental health care</li>
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-semibold">Prohibited Uses</h4>
        <div className="space-y-6">
          <div>
            <h5 className="font-medium mb-2">Harmful Content</h5>
            <p className="mb-2">Do not use FeelHeard.me to promote, encourage, or threaten violence against yourself or others. This includes:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Explicit threats of physical harm</li>
              <li>Detailed discussions of plans to harm yourself or others</li>
              <li>Encouraging self-destructive behaviors</li>
              <li>Glorifying or promoting dangerous activities</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Crisis Situations</h5>
            <p className="mb-2">While we strive to be supportive, FeelHeard.me is not designed for crisis intervention. If you are experiencing a mental health emergency, please contact:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>National Suicide Prevention Lifeline: 988 or 1-800-273-8255</li>
              <li>Crisis Text Line: Text HOME to 741741</li>
              <li>Your local emergency services: 911 (US) or equivalent</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Harassment & Abuse</h5>
            <p className="mb-2">Do not attempt to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Train the AI to produce harmful, discriminatory, or inappropriate responses</li>
              <li>Use the service to develop content that harasses or demeans others</li>
              <li>Create scenarios that promote hatred based on identity or vulnerable groups</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Illegal Activities</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Plan or promote illegal activities</li>
              <li>Share content that violates others' legal rights</li>
              <li>Distribute or solicit illegal materials</li>
              <li>Evade law enforcement or legal obligations</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Spam & Commercial Misuse</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Commercial promotion or advertising</li>
              <li>Mass communication or marketing purposes</li>
              <li>Lead generation or customer recruitment</li>
              <li>Unsolicited promotion of products or services</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-semibold">Account Security Responsibilities</h4>
        <div className="space-y-6">
          <div>
            <h5 className="font-medium mb-2">Password Protection</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Create a strong, unique password that combines letters, numbers, and symbols</li>
              <li>Change your password periodically (we recommend every 3-6 months)</li>
              <li>Never share your password with others, including FeelHeard.me staff</li>
              <li>Do not reuse passwords from other services</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Secure Access</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Log out of your account when using shared or public devices</li>
              <li>Only access FeelHeard.me through the official website or app</li>
              <li>Be cautious of phishing attempts claiming to be from our service</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Breach Notification</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Notify us immediately at security@somatopia.com if you suspect unauthorized access</li>
              <li>Report any unusual activity or conversations you don't recognize</li>
              <li>Alert us if you receive suspicious communications appearing to be from FeelHeard.me</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Device Security</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Keep your device's operating system and browsers updated</li>
              <li>Use security software when possible</li>
              <li>Be mindful of downloading the FeelHeard.me app (coming soon) only from official app stores</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-semibold">Content Shared with AI</h4>
        <div className="space-y-6">
          <div>
            <h5 className="font-medium mb-2">Ownership & Privacy</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>You retain ownership of the personal content you share</li>
              <li>Your conversations are processed according to our Privacy Policy</li>
              <li>Some anonymized data may be used to improve the quality of our service</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Accuracy Responsibilities</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>You are responsible for the accuracy of information you provide</li>
              <li>Our AI may not be able to verify factual claims you make</li>
              <li>Do not rely on the AI for factual verification of critical information</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Sensitive Information</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Avoid sharing highly sensitive personal information (SSN, full financial details, etc.)</li>
              <li>Do not share private information about others without their consent</li>
              <li>Be mindful that while we employ strong security measures, no digital system is 100% secure</li>
            </ul>
          </div>

          <div>
            <h5 className="font-medium mb-2">Content Moderation</h5>
            <ul className="list-disc pl-5 space-y-2">
              <li>Your conversations may be subject to automated review for safety purposes</li>
              <li>In rare cases of severe safety concerns, content may be reviewed by trained professionals</li>
              <li>We prioritize your privacy and only conduct reviews when necessary for safety</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserResponsibilities;